import type { AxiosResponse } from 'axios';
import type { ApiResponse } from '../../models/ApiResponse';
import { isSuccessResponse } from '../../models/ApiResponse';
import type { BulkImportFileDetail, BulkImportUploadResult } from '../../models/bulkImportModel';
import { container, TYPES } from '../inversify.config';
import type { IBulkImportService } from '../interface/IBulkImportService';
import type { IHttpWrapperService } from '../interface/IHttpWrapperService';

export class BulkImportService implements IBulkImportService {
  private httpService: IHttpWrapperService = container.get(TYPES.IHttpWrapperService);

  public downloadTemplate = async (entityType: string, partnerCode: string): Promise<void> => {
    const url =
      `bulkimport/downloadtemplate?entityType=${encodeURIComponent(entityType)}` +
      `&partnerCode=${encodeURIComponent(partnerCode)}`;

    const response = await this.httpService.getBlobRequest(url);
    this.triggerDownload(response, `${entityType}_Template.xlsx`);
  };

  public upload = async (
    file: File,
    entityType: string,
    partnerCode: string,
    customerId: string
  ): Promise<BulkImportUploadResult> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entityType', entityType);
    formData.append('partnerCode', partnerCode);
    formData.append('customerId', customerId);

    const response: AxiosResponse<ApiResponse<BulkImportUploadResult>> =
      await this.httpService.postFormDataRequest<BulkImportUploadResult>('bulkimport/upload', formData);

    if (!isSuccessResponse(response.data) || !response.data.data?.isSuccess) {
      throw new Error(
        response.data.message ||
          response.data.data?.errorMessage ||
          'Failed to upload bulk import file'
      );
    }

    return response.data.data;
  };

  public getStatus = async (fileDetailId: number): Promise<BulkImportFileDetail> => {
    const response: AxiosResponse<ApiResponse<BulkImportFileDetail>> =
      await this.httpService.getRequest<BulkImportFileDetail>(`bulkimport/${fileDetailId}/status`);

    if (!isSuccessResponse(response.data) || !response.data.data) {
      throw new Error(response.data.message || 'Failed to get bulk import status');
    }

    return response.data.data;
  };

  public getHistory = async (
    partnerCode: string,
    entityType: string,
    page = 1,
    pageSize = 20
  ): Promise<BulkImportFileDetail[]> => {
    const url =
      `bulkimport/history?partnerCode=${encodeURIComponent(partnerCode)}` +
      `&entityType=${encodeURIComponent(entityType)}` +
      `&page=${page}&pageSize=${pageSize}`;

    const response: AxiosResponse<ApiResponse<BulkImportFileDetail[]>> =
      await this.httpService.getRequest<BulkImportFileDetail[]>(url);

    if (!isSuccessResponse(response.data)) {
      throw new Error(response.data.message || 'Failed to load bulk import history');
    }

    return response.data.data ?? [];
  };

  public downloadErrors = async (fileDetailId: number): Promise<void> => {
    const response = await this.httpService.getBlobRequest(
      `bulkimport/${fileDetailId}/errors/download`
    );
    this.triggerDownload(response, `BulkImport_Errors_${fileDetailId}.xlsx`);
  };

  private triggerDownload(response: AxiosResponse<Blob>, fallbackFilename: string): void {
    if (response.data.type?.includes('application/json')) {
      throw new Error('Download failed. Please try again.');
    }

    const contentDisposition = response.headers['content-disposition'] as string | undefined;
    let filename = fallbackFilename;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch?.[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    const blobUrl = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  }
}
